/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { posthog } from '@/lib/posthog';

// Use environment variable for API URL, fallback to localhost for development
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'member';
}

interface AuthContextValue {
  user: User | null;
  userId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  login: (returnTo?: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchAuthUser(): Promise<User | null> {
      for (let attempt = 1; attempt <= 2; attempt++) {
        try {
          const res = await fetch(`${API_URL}/auth/me`, { credentials: 'include' });
          if (res.ok) {
            const data = await res.json();
            return data?.user ?? null;
          }
          // Only retry on 5xx (server error during deploy), not 401/403
          if (res.status < 500 || attempt === 2) return null;
        } catch {
          // Network error (server unreachable during deploy)
          if (attempt === 2) return null;
        }
        await new Promise((r) => setTimeout(r, 1500));
      }
      return null;
    }

    fetchAuthUser()
      .then((authUser) => {
        if (cancelled || !authUser) return;
        setUser(authUser);
        // Set Sentry user context for error tracking (only in production)
        if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
          Sentry.setUser({
            id: authUser.id,
            email: authUser.email,
            username: authUser.name,
          });
        }
        // Set PostHog user context for analytics
        if (import.meta.env.VITE_PUBLIC_POSTHOG_KEY) {
          const currentDistinctId = posthog.get_distinct_id();
          const lastKnownUserId = localStorage.getItem('posthog_last_user_id');

          // Only alias if this is a fresh anonymous session (not a previous user's session)
          // - Skip if same user (page refresh): currentDistinctId === user.id
          // - Skip if switching users: lastKnownUserId exists AND differs from new user
          // - Alias if: anonymous session (lastKnownUserId empty OR matches current user)
          const isSameUser = currentDistinctId === authUser.id;
          const isPreviousUserSession = lastKnownUserId && lastKnownUserId !== authUser.id;

          if (!isSameUser && !isPreviousUserSession) {
            try {
              posthog.alias(authUser.id);
            } catch {
              // PostHog alias failures are non-critical - don't break auth flow
            }
          }

          // If switching from a different user, reset first to avoid data leakage
          if (isPreviousUserSession) {
            posthog.reset();
          }

          posthog.identify(authUser.id, {
            email: authUser.email,
            name: authUser.name,
            role: authUser.role,
          });

          // Track this user for future logins on this device
          localStorage.setItem('posthog_last_user_id', authUser.id);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Sign-in only. The client deliberately never sends `screen_hint=signup`:
  // ARLOS is invite-only, and pointing a stranger at Auth0's sign-up tab let
  // them create an identity and verify an email that ARLOS would then refuse.
  // Access is requested through /api/public/access-request instead.
  const login = (returnTo?: string) => {
    // Re-encode returnTo for the auth URL (browser auto-decoded it from searchParams)
    const searchParams = new URLSearchParams();
    if (returnTo) {
      searchParams.set('returnTo', returnTo);
    }
    const query = searchParams.toString();
    window.location.href = `${API_URL}/auth/login${query ? `?${query}` : ''}`;
  };

  const logout = async () => {
    await fetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
    setUser(null);
    // Clear Sentry user context on logout (only in production)
    if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
      Sentry.setUser(null);
    }
    // Clear PostHog user context on logout
    if (import.meta.env.VITE_PUBLIC_POSTHOG_KEY) {
      posthog.reset();
      localStorage.removeItem('posthog_last_user_id');
    }
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userId: user?.id || null,
        isAuthenticated: !!user,
        isLoading,
        isAdmin: user?.role === 'admin',
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
